import { useEffect, useMemo, useState } from 'react';
import { AppShell, ErrorBanner, ProductTopBar, Skeleton, WorkbenchStatusBar, useUser } from '@frm/ui';
import type { ProductTopBarSearchItem } from '@frm/ui';
import { useApps } from '@/api/apps';
import { useRecent } from '@/hooks/useRecent';
import { logoutFromLauncher } from '@/lib/auth';
import { assignLocation, reloadLocation } from '@/lib/browser';
import { computeFeatured } from '@/lib/featured';
import type { AppEntry } from '@/types/app';

import Hero from '@/components/Hero';
import AppCard from '@/components/AppCard';
import RecentStrip from '@/components/RecentStrip';
import SectionHeader from '@/components/SectionHeader';

export default function LauncherPage() {
  const auth = useUser();
  const { user } = auth;
  const isAuthenticated = auth.status === 'authenticated';
  const { data: apps, isLoading, isError, error } = useApps(isAuthenticated);
  const { ids: recentIds, bump, clear } = useRecent();
  const [logoutPending, setLogoutPending] = useState(false);

  // SSO-рефактор: единственная точка входа — /login. Неавторизованных и
  // упавших со «status: error» отправляем туда с ?next=<текущий-путь>, чтобы
  // после успешного логина вернуться ровно сюда. Hero больше не показывает
  // inline-форму.
  useEffect(() => {
    if (auth.status === 'unauthenticated' || auth.status === 'error') {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      assignLocation(`/login?next=${next}`);
    }
  }, [auth.status]);

  const featured = useMemo(
    () => (apps ? computeFeatured(apps, recentIds) : null),
    [apps, recentIds],
  );
  const searchItems = useMemo<ProductTopBarSearchItem[]>(
    () => (apps ?? [])
      .filter((app) => app.status !== 'soon' && app.url)
      .map((app) => ({
        id: app.id,
        label: shortLabel(app.name),
        detail: `${app.description} · ${app.tags.join(', ')}`,
        href: app.url ?? undefined,
      })),
    [apps],
  );

  const open = (app: AppEntry) => {
    if (!app.url || app.status === 'soon') return;
    bump(app.id);
    // Открываем в текущей вкладке — наши сервисы под единым SSO,
    // back-кнопка работает.
    assignLocation(app.url);
  };

  const logout = async () => {
    if (logoutPending) return;
    setLogoutPending(true);
    clear();
    await logoutFromLauncher();
    assignLocation('/login');
  };

  // Пока auth.status === 'loading' (или мы в процессе редиректа на /login)
  // показываем лаконичный shell без TopBar/Hero — избегаем flicker формы.
  if (!isAuthenticated) {
    return (
      <AppShell mode="workbench" contentPreset="standard">
        <div className="launcher-auth-check" aria-live="polite" aria-busy="true">
          <Skeleton width="40%" height={20} />
          <Skeleton width="80%" height={48} />
          <Skeleton width="100%" height={200} radius={12} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      mode="workbench"
      contentPreset="standard"
      topBar={(
        <ProductTopBar
          productLabel="Лаунчер"
          product="launch"
          boardTitle="каталог сервисов"
          user={user}
          onLogout={logout}
          searchPlaceholder="Найти сервис"
          searchItems={searchItems}
          searchDisabled={searchItems.length === 0}
          onSearchItemSelect={(item) => {
            const app = apps?.find((candidate) => candidate.id === item.id);
            if (app) open(app);
          }}
          onRefresh={reloadLocation}
          refreshLabel="Обновить"
          rightSlot={logoutPending ? <span className="topbar-pending">Выходим...</span> : null}
        />
      )}
      footer={<WorkbenchStatusBar connectionSlot="SSO" versionSlot="@frm/ui 1.9.0" />}
    >
      <div>
        <Hero
          userName={user?.displayName ?? user?.login}
          featured={featured}
          hasRecent={recentIds.length > 0}
          onOpenFeatured={open}
        />

        {isAuthenticated && isLoading && (
          <div className="launcher-catalog-loading" aria-busy="true">
            <Skeleton width="30%" height={16} />
            <div className="launcher-catalog-loading__grid">
              <Skeleton width="100%" height={160} radius={12} />
              <Skeleton width="100%" height={160} radius={12} />
              <Skeleton width="100%" height={160} radius={12} />
            </div>
          </div>
        )}

        {isAuthenticated && isError && (
          <ErrorBanner
            tone="danger"
            title="Не удалось загрузить каталог"
            description={error instanceof Error ? error.message : 'unknown'}
          />
        )}

        {isAuthenticated && apps && (
          <>
            <SectionHeader id="recent-services" title="Недавнее" />
            <RecentStrip apps={apps} recentIds={recentIds} onOpen={open} />

            <SectionHeader id="all-services" title="Все сервисы" count={apps.length} />
            <div className="apps-grid">
              {apps.map((app) => (
                <AppCard key={app.id} app={app} onOpen={open} />
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

function shortLabel(name: string): string {
  const m = /^ФРМ[.\s]+(.+)$/i.exec(name.trim());
  return m?.[1] ?? name;
}
